/**
 * The Plan Month card: the month's three counts, set by the user for that
 * month only, then "Plan {month}" or "Not now".
 *
 * Same contract as the questions and verify cards: the backend's PlanMonth
 * tool has no side effects, it echoes the month and its current counts in its
 * result, and that result is what we render. The user's pick goes back as an
 * ordinary user message, and the agent runs StartMonthPlan with it. The
 * message restates the month and every count so it stands on its own.
 */
import type { ChatToolEvent, StreamingToolEvent } from "@/types/chat.type";

export const PLAN_MONTH_TOOL = "PlanMonth";

export type PlanMonthCounts = {
  staticPosts: number;
  verticalVideoPosts: number;
  horizontalVideoPosts: number;
};

export type PlanMonthRequest = PlanMonthCounts & {
  year: number;
  month: number;
  /** "October 2026". */
  monthLabel: string;
  brandName: string | null;
  /** Where the counts come from: the month's own, the strategy's, or the brand's settings. */
  source: "MONTH" | "STRATEGY" | "SETTINGS";
  /** The brand's counts were cut down for a month already under way. */
  prorated: boolean;
};

export type PlanMonthAnswer = { planned: false } | ({ planned: true } & PlanMonthCounts);

export const PLAN_MONTH_FIELDS: { key: keyof PlanMonthCounts; label: string; word: string }[] = [
  { key: "staticPosts", label: "Static", word: "static" },
  { key: "verticalVideoPosts", label: "Vertical video", word: "vertical video" },
  { key: "horizontalVideoPosts", label: "Horizontal video", word: "horizontal video" },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const count = (value: unknown) => (typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null);

/**
 * The card a PlanMonth event puts up, or null if it isn't one. Read from the
 * result only: a refused call (a past month, a second card) carries no counts.
 */
export function parsePlanMonthRequest(
  event: Pick<ChatToolEvent | StreamingToolEvent, "toolName" | "result">,
): PlanMonthRequest | null {
  if (event.toolName !== PLAN_MONTH_TOOL || !isRecord(event.result) || event.result.ok !== true) return null;
  const r = event.result;
  const staticPosts = count(r.staticPosts);
  const verticalVideoPosts = count(r.verticalVideoPosts);
  const horizontalVideoPosts = count(r.horizontalVideoPosts);
  if (typeof r.year !== "number" || typeof r.month !== "number" || typeof r.monthLabel !== "string") return null;
  if (staticPosts === null || verticalVideoPosts === null || horizontalVideoPosts === null) return null;
  return {
    year: r.year,
    month: r.month,
    monthLabel: r.monthLabel,
    brandName: typeof r.brandName === "string" && r.brandName.trim() ? r.brandName.trim() : null,
    source: r.source === "MONTH" || r.source === "STRATEGY" ? r.source : "SETTINGS",
    prorated: r.prorated === true,
    staticPosts,
    verticalVideoPosts,
    horizontalVideoPosts,
  };
}

/** "Plan October 2026 with 5 static, 5 vertical video and 2 horizontal video posts." */
export function formatPlanMonthMessage(request: PlanMonthRequest, counts: PlanMonthCounts): string {
  const parts = PLAN_MONTH_FIELDS.map((field) => `${counts[field.key]} ${field.word}`);
  return `Plan ${request.monthLabel} with ${parts[0]}, ${parts[1]} and ${parts[2]} posts.`;
}

export function notNowMessage(request: PlanMonthRequest): string {
  return `Not now. Don't plan ${request.monthLabel} yet.`;
}

/** What a user message answered on the card, or null when it is not that card's answer. */
export function readPlanMonthAnswer(request: PlanMonthRequest, sent: string): PlanMonthAnswer | null {
  const content = sent.trim();
  if (content === notNowMessage(request)) return { planned: false };
  const match = content.match(
    /^Plan .+ with (\d+) static, (\d+) vertical video and (\d+) horizontal video posts\.$/,
  );
  if (!match) return null;
  return {
    planned: true,
    staticPosts: Number(match[1]),
    verticalVideoPosts: Number(match[2]),
    horizontalVideoPosts: Number(match[3]),
  };
}

/** "Faces’ calendar", "Mohtawa’s calendar". */
export function calendarTitle(brandName: string | null): string {
  if (!brandName) return "The calendar";
  return `${brandName}${/s$/i.test(brandName) ? "’" : "’s"} calendar`;
}

/** The month's name alone: "October". */
export function monthWord(request: Pick<PlanMonthRequest, "monthLabel">): string {
  return request.monthLabel.split(" ")[0] ?? request.monthLabel;
}
